/**
 * Instellingen (SPEC §7.4) met tabbladen: tarieven, afzender, berichten,
 * gebruikers, mailteksten, logboek en de dagronde. De teksten op de site
 * staan in de code (`src/lib/teksten.ts`), zodat test en productie niet uit
 * elkaar lopen.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { api, type Gebruikersregel, type OmgevingInfo, type Voorbeeldmail } from '../../api';
import { Bevestigknop, Knop, Laden, Melding, Veld, useTitel } from '../../componenten/basis';
import { alsEuro, uitInvoer } from '../../lib/geld';
import { ALLE_ROLLEN, ROLNAMEN, magGebruikersBeheren, magInstellingenBeheren, magMuteren, type Rol } from '../../lib/rollen';
import { Logboek } from './Logboek';
import { Berichten } from './Berichten';

const TABS = [
  ['tarieven', 'Tarieven en termijnen'],
  ['afzender', 'Afzender en stichting'],
  ['berichten', 'Berichten'],
  ['gebruikers', 'Gebruikers'],
  ['mails', 'Mailteksten'],
  ['logboek', 'Logboek'],
  ['dagronde', 'Dagronde'],
] as const;

type Tab = (typeof TABS)[number][0];

export function Instellingen({ omgeving, ververs }: { omgeving: OmgevingInfo; ververs: () => Promise<void> }) {
  useTitel('Instellingen');
  const { tab = 'tarieven' } = useParams<{ tab: Tab }>();
  const [data, zetData] = useState<{ instellingen: Record<string, Record<string, unknown>>; ontbreekt: string[] } | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const mag = magInstellingenBeheren(omgeving.gebruiker?.rollen ?? []);

  useEffect(() => {
    api.haal<typeof data>('/api/beheer/instellingen').then(zetData).catch((f: Error) => zetFout(f.message));
  }, []);

  async function bewaar(sleutel: string, waarde: Record<string, unknown>) {
    const uit = await api.stuur<{ instellingen: Record<string, Record<string, unknown>> }>(`/api/beheer/instellingen/${sleutel}`, waarde, 'PUT');
    zetData((d) => (d ? { ...d, instellingen: uit.instellingen } : d));
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl">Instellingen</h1>
      {data && data.ontbreekt.length > 0 && (
        <Melding>
          Deze omgeving mist: {data.ontbreekt.join('; ')}.
        </Melding>
      )}
      <nav className="flex flex-wrap gap-1 border-b border-lijn-zacht pb-3">
        {TABS.map(([sleutel, naam]) => (
          <NavLink
            key={sleutel}
            to={`/beheer/instellingen/${sleutel}`}
            className={({ isActive }) =>
              `rounded-full px-3 py-1.5 text-sm no-underline ${isActive || (sleutel === 'tarieven' && tab === 'tarieven') ? 'bg-vlak-hoog text-goud-300' : 'text-inkt-zacht hover:text-inkt'}`
            }
          >
            {naam}
          </NavLink>
        ))}
      </nav>
      {fout && <Melding soort="fout">{fout}</Melding>}
      {!data && !fout && <Laden />}
      {data && tab === 'tarieven' && <Tarieven i={data.instellingen} bewaar={bewaar} mag={mag} />}
      {data && tab === 'afzender' && <Afzender i={data.instellingen} bewaar={bewaar} mag={mag} />}
      {tab === 'berichten' && <Berichten mag={magMuteren(omgeving.gebruiker?.rollen ?? [])} />}
      {tab === 'gebruikers' && <Gebruikers omgeving={omgeving} />}
      {tab === 'mails' && <Mailteksten mag={magMuteren(omgeving.gebruiker?.rollen ?? [])} />}
      {tab === 'logboek' && <Logboek />}
      {tab === 'dagronde' && <Dagronde omgeving={omgeving} ververs={ververs} />}
    </div>
  );
}

type Bewaar = (sleutel: string, waarde: Record<string, unknown>) => Promise<void>;

function Formulier({ titel, kinderen, onBewaar, mag }: { titel: string; kinderen: React.ReactNode; onBewaar: () => Promise<void>; mag: boolean }) {
  const [bezig, zetBezig] = useState(false);
  const [melding, zetMelding] = useState<string | null>(null);
  async function verstuur(e: FormEvent) {
    e.preventDefault();
    zetBezig(true);
    zetMelding(null);
    try {
      await onBewaar();
      zetMelding('Opgeslagen.');
    } catch (f) {
      zetMelding((f as Error).message);
    } finally {
      zetBezig(false);
    }
  }
  return (
    <form onSubmit={verstuur} className="kaart space-y-4 p-5">
      <h2 className="text-xl">{titel}</h2>
      <fieldset disabled={!mag} className="space-y-4">
        {kinderen}
      </fieldset>
      {mag ? (
        <div className="flex items-center gap-3">
          <Knop type="submit" soort="stil" klein bezig={bezig}>
            Opslaan
          </Knop>
          {melding && <span className="text-sm text-inkt-zacht">{melding}</span>}
        </div>
      ) : (
        <p className="text-xs text-inkt-stil">Alleen de applicatiebeheerder kan dit wijzigen.</p>
      )}
    </form>
  );
}

function Geldveld({ etiket, hulp, waarde, onChange }: { etiket: string; hulp?: string; waarde: number; onChange: (cent: number) => void }) {
  const [tekst, zetTekst] = useState(alsEuro(waarde).replace('€ ', '').replace(',-', ''));
  const cent = uitInvoer(tekst);
  return (
    <Veld etiket={etiket} hulp={hulp} fout={cent === null ? 'Geen bedrag' : null}>
      <div className="flex items-center gap-2">
        <span className="text-inkt-zacht">€</span>
        <input
          className="veld max-w-[10rem]"
          inputMode="decimal"
          value={tekst}
          aria-invalid={cent === null}
          onChange={(e) => {
            zetTekst(e.target.value);
            const c = uitInvoer(e.target.value);
            if (c !== null) onChange(c);
          }}
        />
      </div>
    </Veld>
  );
}

function Getalveld({ etiket, hulp, waarde, onChange }: { etiket: string; hulp?: string; waarde: number; onChange: (n: number) => void }) {
  return (
    <Veld etiket={etiket} hulp={hulp}>
      <input className="veld max-w-[8rem]" type="number" min={0} value={waarde} onChange={(e) => onChange(Number(e.target.value))} />
    </Veld>
  );
}

function Tarieven({ i, bewaar, mag }: { i: Record<string, Record<string, unknown>>; bewaar: Bewaar; mag: boolean }) {
  const [t, zetT] = useState({ ...(i.tarieven as Record<string, number>) });
  const [n, zetN] = useState({ ...(i.niveaus as Record<string, number>) });
  const [c, zetC] = useState({ ...(i.incasso as Record<string, number>) });
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Formulier
        titel="Tarieven"
        mag={mag}
        onBewaar={() => bewaar('tarieven', t)}
        kinderen={
          <>
            <Geldveld etiket="Jaarbijdrage persoon" hulp="Geldt voor nieuwe bijdrageregels; lopende regels houden hun bedrag." waarde={t.jaarbijdrage_cent ?? 0} onChange={(v) => zetT({ ...t, jaarbijdrage_cent: v })} />
            <Geldveld etiket="Jaarbijdrage bedrijf" waarde={t.jaarbijdrage_bedrijf_cent ?? 0} onChange={(v) => zetT({ ...t, jaarbijdrage_bedrijf_cent: v })} />
            <p className="text-xs text-inkt-zacht">Afkopen heeft geen eigen prijs: het is de jaren van het niveau (drempel min één) maal de jaarbijdrage van de held, het lopende jaar inbegrepen. Zilver is dus {(n.zilver_na_jaren ?? 4) - 1} × de jaarbijdrage, goud {(n.goud_na_jaren ?? 6) - 1} ×.</p>
          </>
        }
      />
      <Formulier
        titel="Niveaus"
        mag={mag}
        onBewaar={() => bewaar('niveaus', n)}
        kinderen={
          <>
            <Getalveld etiket="Zilver bij de …e betaalde jaarbijdrage" hulp="4 = na drie volle jaren." waarde={n.zilver_na_jaren ?? 4} onChange={(v) => zetN({ ...n, zilver_na_jaren: v })} />
            <Getalveld etiket="Goud bij de …e betaalde jaarbijdrage" hulp="6 = na vijf volle jaren." waarde={n.goud_na_jaren ?? 6} onChange={(v) => zetN({ ...n, goud_na_jaren: v })} />
            <p className="text-xs text-inkt-zacht">Het niveau wordt uitgerekend, niet opgeslagen: een wijziging hier geldt meteen voor iedereen.</p>
          </>
        }
      />
      <Formulier
        titel="Incasso en termijnen"
        mag={mag}
        onBewaar={() => bewaar('incasso', c)}
        kinderen={
          <>
            <Getalveld etiket="Vooraankondiging, dagen vooraf" hulp="Wettelijk minimum voor SEPA is 14, tenzij anders afgesproken." waarde={c.dagen_vooraf ?? 14} onChange={(v) => zetC({ ...c, dagen_vooraf: v })} />
            <Getalveld etiket="Herinnering na … dagen" waarde={c.herinnering_na_dagen ?? 30} onChange={(v) => zetC({ ...c, herinnering_na_dagen: v })} />
            <Getalveld etiket="Verlopen na … dagen" hulp="Dan gaat de held van de muur, tot hij betaalt." waarde={c.respijt_dagen ?? 60} onChange={(v) => zetC({ ...c, respijt_dagen: v })} />
            <Getalveld etiket="Late instap: vanaf maand …" hulp="Wie zich vanaf deze maand aanmeldt, betaalt voor het volgende kalenderjaar. 13 = nooit." waarde={c.instap_voor_volgend_jaar_vanaf_maand ?? 9} onChange={(v) => zetC({ ...c, instap_voor_volgend_jaar_vanaf_maand: v })} />
          </>
        }
      />
    </div>
  );
}

function Afzender({ i, bewaar, mag }: { i: Record<string, Record<string, unknown>>; bewaar: Bewaar; mag: boolean }) {
  const [a, zetA] = useState({ ...(i.afzender as Record<string, string>) });
  const [s, zetS] = useState({ ...(i.stichting as Record<string, string>) });
  const tekst = (obj: Record<string, string>, zet: (o: Record<string, string>) => void, sleutel: string, etiket: string, hulp?: string) => (
    <Veld etiket={etiket} hulp={hulp}>
      <input className="veld" value={obj[sleutel] ?? ''} onChange={(e) => zet({ ...obj, [sleutel]: e.target.value })} />
    </Veld>
  );
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Formulier
        titel="Afzender van de mail"
        mag={mag}
        onBewaar={() => bewaar('afzender', a)}
        kinderen={
          <>
            {tekst(a, zetA, 'naam', 'Naam boven de mail')}
            {tekst(a, zetA, 'adres', 'Afzenderadres', 'Moet bij Resend geverifieerd zijn.')}
            {tekst(a, zetA, 'beantwoord_aan', 'Antwoordadres')}
            {tekst(a, zetA, 'contactpersoon', 'Contactpersoon in de voet', '“Vragen? Mail … op …”')}
          </>
        }
      />
      <Formulier
        titel="De Stichting"
        mag={mag}
        onBewaar={() => bewaar('stichting', s)}
        kinderen={
          <>
            {tekst(s, zetS, 'naam', 'Naam')}
            {tekst(s, zetS, 'groep', 'Groep, kort')}
            {tekst(s, zetS, 'groep_volledig', 'Groep, voluit')}
            {tekst(s, zetS, 'plaats', 'Plaats')}
            {tekst(s, zetS, 'iban', 'IBAN', 'Alleen getoond op de betaalpagina, voor wie zelf overmaakt.')}
            {tekst(s, zetS, 'tenaamstelling', 'Tenaamstelling', 'Ook op facturen, bij het overmaken.')}
            {tekst(s, zetS, 'btw_vermelding', 'Btw-regel op facturen', 'Komt onder het totaal, bijvoorbeeld welke vrijstelling geldt. Leeg = geen regel. Laat de penningmeester dit vaststellen.')}
          </>
        }
      />
    </div>
  );
}

function Gebruikers({ omgeving }: { omgeving: OmgevingInfo }) {
  const [lijst, zetLijst] = useState<Gebruikersregel[] | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [nieuw, zetNieuw] = useState({ naam: '', email: '', rollen: ['meelezer'] as Rol[] });
  const mag = magGebruikersBeheren(omgeving.gebruiker?.rollen ?? []);

  useEffect(() => {
    api.haal<Gebruikersregel[]>('/api/beheer/gebruikers').then(zetLijst).catch((f: Error) => zetFout(f.message));
  }, []);

  async function wijzig(id: string, w: Partial<Gebruikersregel>) {
    try {
      zetLijst(await api.stuur<Gebruikersregel[]>(`/api/beheer/gebruikers/${id}`, w, 'PUT'));
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  async function verwijder(id: string) {
    try {
      zetLijst(await api.stuur<Gebruikersregel[]>(`/api/beheer/gebruikers/${id}`, {}, 'DELETE'));
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  async function voegToe(e: FormEvent) {
    e.preventDefault();
    try {
      zetLijst(await api.stuur<Gebruikersregel[]>('/api/beheer/gebruikers', nieuw));
      zetNieuw({ naam: '', email: '', rollen: ['meelezer'] });
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  if (fout) return <Melding soort="fout">{fout}</Melding>;
  if (!lijst) return <Laden />;

  return (
    <div className="space-y-4">
      <div className="kaart overflow-x-auto">
        <table className="tabel">
          <thead>
            <tr>
              <th>Naam</th>
              <th>E-mail</th>
              <th>Rollen</th>
              <th>Laatst ingelogd</th>
              <th>Actief</th>
              {mag && <th />}
            </tr>
          </thead>
          <tbody>
            {lijst.map((g) => (
              <tr key={g.id} className={g.actief ? '' : 'opacity-50'}>
                <td>{g.naam}</td>
                <td className="text-inkt-zacht">{g.email}</td>
                <td>
                  <span className="flex flex-wrap gap-2">
                    {ALLE_ROLLEN.map((r) => (
                      <label key={r} className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          className="accent-goud-500"
                          disabled={!mag}
                          checked={g.rollen.includes(r)}
                          onChange={(e) => wijzig(g.id, { rollen: e.target.checked ? [...g.rollen, r] : g.rollen.filter((x) => x !== r) })}
                        />
                        {ROLNAMEN[r]}
                      </label>
                    ))}
                  </span>
                </td>
                <td className="text-inkt-zacht">{g.laatst_ingelogd_op ? new Date(g.laatst_ingelogd_op).toLocaleDateString('nl-NL') : '-'}</td>
                <td>
                  <input type="checkbox" className="accent-goud-500" disabled={!mag} checked={g.actief} onChange={(e) => wijzig(g.id, { actief: e.target.checked })} />
                </td>
                {mag && (
                  <td className="text-right opacity-100">
                    {!g.actief && g.email !== omgeving.gebruiker?.email && (
                      <Bevestigknop vraag={`${g.naam} definitief verwijderen?`} onBevestig={() => verwijder(g.id)}>
                        Verwijderen
                      </Bevestigknop>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mag && (
        <form onSubmit={voegToe} className="kaart flex flex-wrap items-end gap-3 p-4">
          <Veld etiket="Naam">
            <input className="veld" required value={nieuw.naam} onChange={(e) => zetNieuw({ ...nieuw, naam: e.target.value })} />
          </Veld>
          <Veld etiket="E-mail">
            <input className="veld" type="email" required value={nieuw.email} onChange={(e) => zetNieuw({ ...nieuw, email: e.target.value })} />
          </Veld>
          <Veld etiket="Rol">
            <select className="veld" value={nieuw.rollen[0]} onChange={(e) => zetNieuw({ ...nieuw, rollen: [e.target.value as Rol] })}>
              {ALLE_ROLLEN.map((r) => (
                <option key={r} value={r}>
                  {ROLNAMEN[r]}
                </option>
              ))}
            </select>
          </Veld>
          <Knop type="submit" soort="stil">
            Toevoegen
          </Knop>
        </form>
      )}
    </div>
  );
}

function Mailteksten({ mag }: { mag: boolean }) {
  const [mails, zetMails] = useState<Voorbeeldmail[] | null>(null);
  const [aan, zetAan] = useState('');
  const [melding, zetMelding] = useState<string | null>(null);
  useEffect(() => {
    api.haal<Voorbeeldmail[]>('/api/beheer/mailteksten').then(zetMails).catch(() => zetMails([]));
  }, []);
  if (!mails) return <Laden />;
  return (
    <div className="space-y-4">
      {mag && (
        <form
          className="kaart flex flex-wrap items-end gap-3 p-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const uit = await api.stuur<{ aan: string[] }>('/api/beheer/testmail', { aan });
              zetMelding(`Testbericht verstuurd naar ${uit.aan.join(', ')}.`);
            } catch (f) {
              zetMelding((f as Error).message);
            }
          }}
        >
          <Veld etiket="Testbericht sturen naar" hulp="Leeg = jezelf. In testmodus gaat alles naar het omleidingsadres.">
            <input className="veld" type="email" value={aan} onChange={(e) => zetAan(e.target.value)} />
          </Veld>
          <Knop type="submit" soort="stil">
            Stuur testbericht
          </Knop>
          {melding && <span className="text-sm text-inkt-zacht">{melding}</span>}
        </form>
      )}
      {mails.map((m) => (
        <details key={m.titel} className="kaart p-4">
          <summary className="cursor-pointer">
            <span className="font-medium">{m.titel}</span>
            <span className="ml-2 text-sm text-inkt-zacht">{m.wanneer}</span>
          </summary>
          <p className="mt-3 text-xs text-inkt-zacht">
            Aan: {m.aan} · Onderwerp: {m.onderwerp}
          </p>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-inkt">{m.tekst}</pre>
        </details>
      ))}
    </div>
  );
}

function Dagronde({ omgeving, ververs }: { omgeving: OmgevingInfo; ververs: () => Promise<void> }) {
  const [uitslag, zetUitslag] = useState<Record<string, unknown> | null>(null);
  const [bezig, zetBezig] = useState(false);
  const [datum, zetDatum] = useState(omgeving.klok?.datum ?? '');
  const [fout, zetFout] = useState<string | null>(null);
  const mag = magMuteren(omgeving.gebruiker?.rollen ?? []);

  async function draai() {
    zetBezig(true);
    zetFout(null);
    try {
      zetUitslag(await api.stuur<Record<string, unknown>>('/api/beheer/dagronde'));
    } catch (f) {
      zetFout((f as Error).message);
    } finally {
      zetBezig(false);
    }
  }

  async function zetKlok(nieuw: string | null) {
    try {
      await api.stuur('/api/beheer/testklok', { datum: nieuw }, 'PUT');
      await ververs();
      zetDatum(nieuw ?? '');
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="kaart space-y-4 p-5">
        <h2 className="text-xl">De dagronde</h2>
        <p className="text-sm text-inkt-zacht">
          Elke ochtend om 07:00 doet de app dit vanzelf: aankondigen, incasseren, de stand bij Mollie nalopen, herinneren, verlopen zetten en opruimen. Met de hand draaien doet precies hetzelfde, en nooit iets dubbel.
        </p>
        {mag && (
          <Knop onClick={draai} bezig={bezig}>
            Draai de dagronde nu
          </Knop>
        )}
        {fout && <Melding soort="fout">{fout}</Melding>}
        {uitslag && (
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(uitslag)
              .filter(([k]) => k !== 'fouten')
              .map(([k, v]) => (
                <div key={k} className="rounded-lg bg-nacht px-3 py-2">
                  <dt className="text-xs uppercase tracking-wider text-inkt-zacht">{k}</dt>
                  <dd className="font-display text-lg">{String(v ?? '-')}</dd>
                </div>
              ))}
            {Array.isArray(uitslag.fouten) && uitslag.fouten.length > 0 && (
              <div className="col-span-2">
                <Melding soort="fout">{(uitslag.fouten as string[]).join(' · ')}</Melding>
              </div>
            )}
          </dl>
        )}
      </section>
      {omgeving.testmodus && (
        <section className="kaart space-y-4 border-dashed border-test/40 p-5">
          <h2 className="text-xl text-test">Testklok</h2>
          <p className="text-sm text-inkt-zacht">
            Verzet de datum waarop de app denkt te leven, draai de dagronde, en zie wat er gebeurt: aankondigingen, incasso's, niveaus. Staat alleen in de testmodus.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <Veld etiket="Datum">
              <input className="veld" type="date" value={datum} onChange={(e) => zetDatum(e.target.value)} />
            </Veld>
            <Knop soort="stil" onClick={() => zetKlok(datum || null)}>
              Zet de klok
            </Knop>
            <Knop soort="stil" onClick={() => zetKlok(null)}>
              Terug naar vandaag
            </Knop>
          </div>
          {omgeving.klok?.verzet && <Melding>De klok staat op {omgeving.klok.datum}.</Melding>}
          <p className="text-xs text-inkt-stil">
            Zonder Mollie-sleutel doet de nagebootste bank mee: een incasso slaagt, behalve voor een held met “+stornering” in zijn e-mailadres.
          </p>
        </section>
      )}
    </div>
  );
}
